/**
 * Pure calculation for offer totals.
 * No DB or React dependencies so it can be unit-tested directly.
 *
 * Business rules (VIB-494, utvidet VIB-555, VIB-2086/2090):
 * - Internal hourly lines: amount = effectiveHours × hourly_rate
 *   effectiveHours = quantity hvis unit='t', quantity × BILLABLE_HOURS_PER_DAY hvis unit='d'
 * - Internal fixed og external: amount er satt manuelt (eller quantity × unit_price)
 * - Subtotal (overhead-grunnlag) = sum av linjer. Nye tilbud
 *   (`include_travel_in_overhead !== false`) inkluderer phase="reise"
 *   (VIB-2086). Eksisterende kontrakter med flagget false holder reise
 *   utenfor og legger travel_total på etter overhead/rabatt.
 * - Travel total = sum av lines med phase == "reise". Herav når flagget
 *   er true; addend når flagget er false.
 * - Overhead amount = subtotal × overhead_pct / 100
 * - After overhead = subtotal + overhead_amount
 * - Discount amount = offers.discount_amount if > 0, else subtotal × discount_pct / 100
 * - Total = after_overhead − discount_amount  [+ travel_total hvis flagget er false]
 *
 * GEEK (VIB-2090, speiler SOT / calcLiveBfEstimated):
 * - bf = total − Σ purchase_price på eksterne linjer
 * - Når alt er internt uten innkjøp: GEEK = totalsum inkl. overhead
 *
 * Estimert timepris (speiler «Est. timepris» på prosjektkortet,
 * BF estimert / solgte timer):
 * - Totale interne timer = sum av effectiveHours for internal hourly-linjer
 * - estHourlyRate = GEEK / interne timer = (total − innkjøp) / interne timer
 *   Innkjøp på eksterne linjer trekker altså timeprisen ned; det var ikke
 *   tilfelle med den gamle formelen total / interne timer.
 */

export type OfferPhase = "pl" | "preprod" | "production" | "postprod" | "stills" | "reise";

/**
 * Fakturerbar dag — det kunden BETALER for per "1d" i tilbud (8 timer per
 * dag). Skal IKKE forveksles med WORK_HOURS_PER_DAY i `billing-calculations.ts`
 * (7.5t), som er intern arbeidstid for kapasitetsberegning.
 */
export const BILLABLE_HOURS_PER_DAY = 8;

/**
 * Overtidstillegg på en tilbudslinje (VIB-663f). Hovedlinjens quantity er
 * total antall enheter og unit_price er normal-satsen — overtid er et
 * RENT TILLEGG på et delsegment av timene:
 *
 *   amount = quantity × unit_price + overtime_qty × overtime_extra_rate
 *
 * Eksempel: 12t totalt à 2 098 hvorav 4t er overtid à 500 ekstra:
 *   normal-amount  = 12 × 2 098 = 25 176
 *   overtid-tillegg = 4 × 500   = 2 000
 *   total          = 27 176
 *
 * NULL = ingen overtid på linja.
 */
export interface OvertimeSplit {
  /** Antall enheter (t/d) av total-mengden som er overtid. */
  overtime_qty: number;
  /** Ekstra kr per enhet (på toppen av normal-sats) for overtidstimene. */
  overtime_extra_rate: number;
  /**
   * Enhet for overtidstillegget, uavhengig av hovedlinjen (VIB-2087).
   * Mangler på legacy-rader → treffes som "t" (eller linjens t/d i UI).
   */
  overtime_unit?: "t" | "d";
}

export interface OfferLineForCalc {
  phase: OfferPhase;
  cost_type: string; // 'internal' | 'external'
  pricing_type: "hourly" | "fixed";
  quantity: number;
  unit: string;
  unit_price: number;
  purchase_price: number;
  amount: number;
  /** Overtidssplit (VIB-663f). NULL/undefined = ingen split. */
  overtime_split?: OvertimeSplit | null;
}

export interface OfferForCalc {
  overhead_pct: number;
  discount_pct: number;
  discount_amount: number;
  /**
   * VIB-2086: reise i overhead-grunnlaget. Mangler/undefined = true (ny SOT
   * i tester og nye tilbud). false = gammel formel, ikke tilbakevirkende.
   */
  include_travel_in_overhead?: boolean;
}

export interface OfferTotals {
  /** Sum av linjer i overhead-grunnlaget. Inkl. reise når flagget er true. */
  subtotal: number;
  overhead_amount: number;
  after_overhead: number;
  discount_amount: number;
  /** Sum av reise-linjer. Herav når flagget er true; addend når false. */
  travel_total: number;
  /** subtotal + overhead − discount [+ travel når flagget er false]. */
  total: number;
  perPhase: Record<OfferPhase, number>;
  internalHours: number;
  internalRevenue: number;
  externalCost: number;
  externalRevenue: number;
  bf: number;
  estHourlyRate: number | null;
}

/**
 * Type-guard: returner en gyldig overtime_split eller null.
 *
 * - Aksepterer både nytt format ({ overtime_qty, overtime_extra_rate })
 *   og legacy-format ({ normal_qty, normal_rate, overtime_qty, overtime_rate })
 *   — sistnevnte konverteres on-the-fly slik at vi slipper en
 *   migrasjon for et håndfullt eksisterende rader.
 * - Returnerer null hvis split er tom (overtime_qty <= 0).
 */
export function readOvertimeSplit(
  line: Pick<OfferLineForCalc, "overtime_split">,
): OvertimeSplit | null {
  const s = line.overtime_split as
    | (OvertimeSplit & {
        normal_qty?: number | string;
        normal_rate?: number | string;
        overtime_rate?: number | string;
      })
    | null
    | undefined;
  if (!s) return null;
  const oq = Number(s.overtime_qty) || 0;
  if (oq <= 0) return null;

  // Legacy-format: utled extra_rate som diff mellom overtime_rate og normal_rate.
  if (s.overtime_extra_rate === undefined && s.overtime_rate !== undefined) {
    const overtimeRate = Number(s.overtime_rate) || 0;
    const normalRate = Number(s.normal_rate) || 0;
    return {
      overtime_qty: oq,
      overtime_extra_rate: Math.max(0, overtimeRate - normalRate),
      overtime_unit: s.overtime_unit === "d" ? "d" : "t",
    };
  }

  return {
    overtime_qty: oq,
    overtime_extra_rate: Number(s.overtime_extra_rate) || 0,
    overtime_unit: s.overtime_unit === "d" ? "d" : "t",
  };
}

export function overtimeUnitOf(
  split: Pick<OvertimeSplit, "overtime_unit"> | null | undefined,
  fallbackUnit: string = "t",
): "t" | "d" {
  if (split?.overtime_unit === "d" || split?.overtime_unit === "t") {
    return split.overtime_unit;
  }
  const u = normalizeOfferUnit(fallbackUnit);
  return u === "d" ? "d" : "t";
}

/**
 * Timer-bidrag fra en internal hourly-linje: qty-t direkte, qty-d × 8.
 * I nytt overtid-som-tillegg-format er hovedlinjens quantity uendret —
 * den representerer fortsatt total mengde, derfor regnes timer rett
 * fra line.quantity uavhengig av om en overtidsplit er aktiv.
 */
export function effectiveHoursFromLine(
  line: Pick<OfferLineForCalc, "unit" | "quantity">,
): number {
  const q = line.quantity ?? 0;
  const unit = line.unit === "dag" ? "d" : line.unit;
  if (unit === "d") return q * BILLABLE_HOURS_PER_DAY;
  return q;
}

/**
 * Computes the effective amount for a line.
 * amount = quantity × unit_price for ALLE linjer — unit_price tolkes som
 * "pris per enhet" (kr/t eller kr/d for hourly, kr/stk ellers). Toggel
 * t↔d i UI må derfor konvertere unit_price samtidig (×8 eller ÷8).
 *
 * Fallback: hvis unit_price er 0 for internal hourly, bruk system hourly_rate
 * × effektive timer (bakoverkompat for eksisterende data uten satt pris).
 */
export function deriveLineAmount(
  line: Pick<
    OfferLineForCalc,
    "cost_type" | "pricing_type" | "quantity" | "unit" | "unit_price" | "overtime_split"
  >,
  hourlyRate: number,
): number {
  const qty = line.quantity ?? 0;
  const price = line.unit_price ?? 0;
  // Overtid er et tillegg på hovedlinjens beløp (VIB-663f-fix):
  //   amount = qty × unit_price + overtime_qty × overtime_extra_rate
  // Når unit_price er 0 for internal hourly faller vi tilbake til system-
  // sats × effektive timer (bakoverkompat), og legger overtidstillegget
  // på toppen.
  const split = readOvertimeSplit(line);
  const overtimeAddon = split
    ? Math.round(split.overtime_qty * split.overtime_extra_rate)
    : 0;

  if (price > 0) {
    return Math.round(qty * price) + overtimeAddon;
  }
  if (line.cost_type === "internal" && line.pricing_type === "hourly") {
    const hours = effectiveHoursFromLine(line);
    return Math.round(hours * hourlyRate) + overtimeAddon;
  }
  return Math.round(qty * price) + overtimeAddon;
}

/**
 * Per-section subtotal — sum av amount for linjer i seksjonen (uten overhead/rabatt).
 * VIB-663e: tilvalg vises som egen sub-totalsum i UI og eksport.
 *
 * Reise-linjer telles inn i seksjonens totalsum hvis de er plassert i en
 * tilvalgsseksjon (logikken er at tilvalget skal vise det kunden faktisk
 * betaler for tilvalget, inkl. evt. reise dedikert til tilvalget).
 */
export function calcSectionSubtotal(
  lines: Array<Pick<OfferLineForCalc, "amount">>,
): number {
  return lines.reduce((s, l) => s + (l.amount ?? 0), 0);
}

/**
 * Per-seksjon totaler med overhead/rabatt anvendt på seksjonens linjer
 * (VIB-700). Bruker samme offer-level overhead/discount-prosenter som
 * hovedbudsjettet — Plan A støtter ikke per-seksjon overstyring.
 *
 * Reise-linjer inngår i overhead/rabatt-grunnlaget når
 * `include_travel_in_overhead !== false` (VIB-2086), samme regel som
 * `calcOfferTotals`. Hovedbruken er `OfferLineTable`-seksjons-headere
 * og `OfferBudgetImage`-eksport av enkelttilvalg.
 */
function travelInOverheadBase(offer: OfferForCalc): boolean {
  return offer.include_travel_in_overhead !== false;
}

function overheadBaseAmount(
  lines: OfferLineForCalc[],
  includeTravel: boolean,
): number {
  return lines
    .filter((l) => includeTravel || l.phase !== "reise")
    .reduce((s, l) => s + (l.amount ?? 0), 0);
}

export function calcSectionTotals(
  offer: OfferForCalc,
  lines: OfferLineForCalc[],
): Pick<
  OfferTotals,
  "subtotal" | "overhead_amount" | "discount_amount" | "travel_total" | "total"
> {
  const includeTravel = travelInOverheadBase(offer);
  const travelLines = lines.filter((l) => l.phase === "reise");

  const subtotal = overheadBaseAmount(lines, includeTravel);
  const travel_total = travelLines.reduce((s, l) => s + (l.amount ?? 0), 0);

  const overhead_pct = offer.overhead_pct ?? 0;
  const overhead_amount = Math.round((subtotal * overhead_pct) / 100);

  let discount_amount = 0;
  if (offer.discount_amount && offer.discount_amount > 0) {
    // Beløp-rabatten er en GLOBAL rabatt på hele tilbudet, så vi anvender
    // den ikke per seksjon (ville dobbeltrabattert). Bare prosent-rabatt
    // skalerer naturlig med seksjons-sub-summen.
    discount_amount = 0;
  } else if (offer.discount_pct && offer.discount_pct > 0) {
    discount_amount = Math.round((subtotal * offer.discount_pct) / 100);
  }

  const total =
    subtotal + overhead_amount - discount_amount + (includeTravel ? 0 : travel_total);
  return { subtotal, overhead_amount, discount_amount, travel_total, total };
}

export function calcOfferTotals(
  offer: OfferForCalc,
  lines: OfferLineForCalc[],
): OfferTotals {
  // VIB-2086: nye tilbud tar reise inn i overhead/rabatt-grunnlaget.
  // travel_total er da herav, ikke addend (ellers dobbeltelling).
  // include_travel_in_overhead === false: gammel formel, ikke tilbakevirkende.
  const includeTravel = travelInOverheadBase(offer);
  const travelLines = lines.filter((l) => l.phase === "reise");

  const subtotal = overheadBaseAmount(lines, includeTravel);
  const travel_total = travelLines.reduce((s, l) => s + (l.amount ?? 0), 0);

  const overhead_pct = offer.overhead_pct ?? 0;
  const overhead_amount = Math.round((subtotal * overhead_pct) / 100);
  const after_overhead = subtotal + overhead_amount;

  let discount_amount = 0;
  if (offer.discount_amount && offer.discount_amount > 0) {
    discount_amount = Math.round(offer.discount_amount);
  } else if (offer.discount_pct && offer.discount_pct > 0) {
    discount_amount = Math.round((subtotal * offer.discount_pct) / 100);
  }

  const total =
    after_overhead - discount_amount + (includeTravel ? 0 : travel_total);

  const perPhase: Record<OfferPhase, number> = {
    pl: 0,
    preprod: 0,
    production: 0,
    postprod: 0,
    stills: 0,
    reise: 0,
  };

  let internalHours = 0;
  let internalRevenue = 0;
  let externalCost = 0;
  let externalRevenue = 0;

  for (const line of lines) {
    perPhase[line.phase] += line.amount ?? 0;

    if (line.cost_type === "internal" && line.pricing_type === "hourly") {
      internalHours += effectiveHoursFromLine(line);
      internalRevenue += line.amount ?? 0;
    } else if (line.cost_type === "internal") {
      internalRevenue += line.amount ?? 0;
    } else {
      externalRevenue += line.amount ?? 0;
      externalCost += line.purchase_price ?? 0;
    }
  }

  // VIB-2090: GEEK = totalsum − eksterne innkjøp. Speiler SOT
  // (total − est. kost på eksterne). Interne inntekter uten overhead
  // var den gamle, ufullstendige formelen.
  const bf = total - externalCost;
  // Est. timepris = dekningsbidrag per intern time, samme nevner og teller
  // som prosjektkortet (BF estimert / solgte timer). Innkjøp er trukket fra.
  const estHourlyRate = internalHours > 0 ? Math.round(bf / internalHours) : null;

  return {
    subtotal,
    overhead_amount,
    after_overhead,
    discount_amount,
    travel_total,
    total,
    perPhase,
    internalHours,
    internalRevenue,
    externalCost,
    externalRevenue,
    bf,
    estHourlyRate,
  };
}

/**
 * Bygg patch-objektet som skal sendes til onChange når overtidstillegget
 * endres på en linje. Hovedlinjens quantity og unit_price er uendret —
 * vi oppdaterer bare overtime_split og rekalkulerer amount.
 */
export function patchFromOvertimeSplit(
  split: OvertimeSplit,
  baseQty: number,
  baseUnitPrice: number,
): {
  overtime_split: OvertimeSplit;
  amount: number;
} {
  const amount = Math.round(
    baseQty * baseUnitPrice + split.overtime_qty * split.overtime_extra_rate,
  );
  return { overtime_split: split, amount };
}

/**
 * Foreslå en initial OvertimeSplit når PL aktiverer overtid for første
 * gang på en linje. Default overtime_qty = 0 og extra_rate = template-
 * default (kr/t) skalert til `overtimeUnit`. Bevisst qty=0, så PL fyller inn.
 */
export function suggestOvertimeSplit(
  templateDefaultExtraRate?: number | null,
  overtimeUnit: "t" | "d" = "t",
): OvertimeSplit {
  const hourly = Math.max(0, Math.round(Number(templateDefaultExtraRate) || 0));
  const extra =
    overtimeUnit === "d" ? Math.round(hourly * BILLABLE_HOURS_PER_DAY) : hourly;
  return {
    overtime_qty: 0,
    overtime_extra_rate: extra,
    overtime_unit: overtimeUnit,
  };
}

/**
 * Bytt enhet på overtidstillegget (VIB-2087). Qty og tilleggssats
 * skalerer ×8 / ÷8 slik at beløpet bevares (samme regel som linjens t↔d).
 */
export function convertOvertimeSplitUnit(
  split: OvertimeSplit,
  toUnit: "t" | "d",
): OvertimeSplit {
  const fromUnit = overtimeUnitOf(split);
  if (fromUnit === toUnit) {
    return { ...split, overtime_unit: toUnit };
  }
  if (fromUnit === "t" && toUnit === "d") {
    return {
      overtime_qty: split.overtime_qty / BILLABLE_HOURS_PER_DAY,
      overtime_extra_rate: Math.round(split.overtime_extra_rate * BILLABLE_HOURS_PER_DAY),
      overtime_unit: "d",
    };
  }
  return {
    overtime_qty: split.overtime_qty * BILLABLE_HOURS_PER_DAY,
    overtime_extra_rate: Math.round(split.overtime_extra_rate / BILLABLE_HOURS_PER_DAY),
    overtime_unit: "t",
  };
}

/**
 * Slå sammen Tilbudsskriving-credit-linja med Prosjektledelse i
 * eksport-kontekst (kopier-bilde og PPTX). Kunden ser ikke den auto-
 * genererte credit-linja separat — i stedet vokser hovedlinja
 * "Prosjektledelse" med credit-timer/-amount. Editoren beholder linja
 * som synlig (låst) så ansatte ser hva systemet beregner.
 *
 * Hvis det ikke finnes en "Prosjektledelse"-linje fra før, blir auto-
 * linja vist som "Prosjektledelse" istedenfor "Tilbudsskriving".
 *
 * VIB-2089: auto-linja er alltid i timer. Når mål-linja er i dager,
 * konverteres timene med BILLABLE_HOURS_PER_DAY (8) før summering —
 * 2d + 4t = 2,5d, ikke 6d.
 */
export interface OfferLineLike {
  description: string;
  phase: string;
  quantity: number;
  amount: number;
  unit?: string;
  /** Pris per gjeldende enhet. Må følge med når `unit` endres. */
  unit_price?: number;
  is_auto_generated?: boolean;
  auto_kind?: string | null;
}

/**
 * Enhetsbytte på en intern linje (VIB-2029, omgjort i VIB-2097).
 *
 * `unit_price` er ALLTID pris per gjeldende enhet, og amount = qty × unit_price.
 *
 * - t ↔ d konverterer gjeldende sats (×8 / ÷8, fakturerbar dag = 8t).
 * - Til fast/stk: sats og beløp beholdes (klumpsum = det linjen kostet).
 * - Fra fast/stk til t/d: en klumpsum har ingen tidsbasis, så satsen hentes
 *   fra `hourlyBasis` (kr/t): t = basis, d = basis × 8. Basisen er linjens
 *   lagrede `rate_basis_hourly` (satt sist linjen sto på t/d), ellers
 *   katalogens timesats. Uten basis (0) beholdes satsen som etikettbytte.
 *
 * Dermed går enhetscellens syklus t → d → fast → t opp uansett antall
 * runder: d 10 000 → fast 10 000 → t 1 250 → d 10 000. Beslutning Erik
 * 02.09 etter preview-test (VIB-2097), da katalog-basis alene mistet en
 * manuelt justert sats etter fast.
 *
 * «dag» normaliseres til «d» (legacy/katalog-varianter).
 */
export function normalizeOfferUnit(unit: string): string {
  if (unit === "dag") return "d";
  return unit;
}

export function isOfferTimeUnit(unit: string): boolean {
  const u = normalizeOfferUnit(unit);
  return u === "t" || u === "d" || u === "fast";
}

function isHourlyOrDaily(unit: string): boolean {
  return unit === "t" || unit === "d";
}

/**
 * Tidsbasis (kr/t) fra en sats per enhet: t → sats, d → sats ÷ 8, ellers null
 * (fast/stk har ingen tidsbasis). Lagres som `rate_basis_hourly` på linjen.
 */
export function hourlyBasisFromUnitPrice(unit: string, unitPrice: number): number | null {
  const u = normalizeOfferUnit(unit);
  const price = Number(unitPrice) || 0;
  if (price <= 0) return null;
  if (u === "t") return price;
  if (u === "d") return price / BILLABLE_HOURS_PER_DAY;
  return null;
}

/**
 * Sats per enhet slik raden faktisk regner med den: lagret sats hvis satt,
 * ellers system-/katalog-timesats skalert til enheten (×8 for dag). Uten
 * skaleringen ble en dag-linje uten sats konvertert som om timesatsen var
 * dagsats (÷8 ved d → t).
 */
export function effectiveUnitPrice(
  unit: string,
  unitPrice: number,
  hourlyRate: number,
): number {
  const price = Number(unitPrice) || 0;
  if (price > 0) return price;
  const u = normalizeOfferUnit(unit);
  const rate = Number(hourlyRate) || 0;
  if (u === "d") return rate * BILLABLE_HOURS_PER_DAY;
  if (u === "t") return rate;
  return 0;
}

export function convertOfferLineUnit(input: {
  fromUnit: string;
  toUnit: string;
  quantity: number;
  unitPrice: number;
  /**
   * Tidsbasis (kr/t) for linjen: lagret `rate_basis_hourly`, ellers
   * katalogens timesats. Brukes kun fra fast/stk → t/d.
   */
  hourlyBasis?: number | null;
}): { unit: string; unit_price: number; amount: number; rate_basis_hourly: number | null } {
  const fromUnit = normalizeOfferUnit(input.fromUnit);
  const toUnit = normalizeOfferUnit(input.toUnit);
  const q = Number(input.quantity) || 0;
  const price = Number(input.unitPrice) || 0;
  const basis = Number(input.hourlyBasis) || 0;

  let newPrice = price;
  let rateBasis: number | null = basis > 0 ? basis : null;
  if (isHourlyOrDaily(fromUnit)) {
    // Linjen står på t/d: satsen ER tidsbasisen, husk den.
    rateBasis = hourlyBasisFromUnitPrice(fromUnit, price) ?? rateBasis;
    if (fromUnit === "t" && toUnit === "d") {
      newPrice = Math.round(price * BILLABLE_HOURS_PER_DAY);
    } else if (fromUnit === "d" && toUnit === "t") {
      newPrice = Math.round(price / BILLABLE_HOURS_PER_DAY);
    }
  } else if (isHourlyOrDaily(toUnit) && basis > 0) {
    newPrice =
      toUnit === "d" ? Math.round(basis * BILLABLE_HOURS_PER_DAY) : Math.round(basis);
  }

  return {
    unit: toUnit,
    unit_price: newPrice,
    amount: Math.round(q * newPrice),
    rate_basis_hourly: rateBasis,
  };
}

export function mergeOfferWritingForExport<T extends OfferLineLike>(lines: T[]): T[] {
  const auto = lines.find(
    (l) => l.is_auto_generated && l.auto_kind === "offer_writing",
  );
  if (!auto || (auto.quantity ?? 0) <= 0) {
    // Ingen credit eller 0t — bare filtrer vekk auto-linja.
    return lines.filter(
      (l) => !(l.is_auto_generated && l.auto_kind === "offer_writing"),
    );
  }

  const filtered = lines.filter(
    (l) => !(l.is_auto_generated && l.auto_kind === "offer_writing"),
  );

  const targetIdx = filtered.findIndex(
    (l) => l.phase === "pl" && l.description.toLowerCase() === "prosjektledelse",
  );

  if (targetIdx >= 0) {
    const target = filtered[targetIdx];
    const targetUnit = normalizeOfferUnit(target.unit ?? "t");
    const autoHours = quantityToHours(auto.quantity ?? 0, auto.unit ?? "t");
    if (isHourlyOrDaily(targetUnit)) {
      // Summen regnes i timer og VISES i timer, også når linja står i dager.
      // Tilbudsskriving er sjelden et helt antall dager, så en dag-linje ga
      // «3,13 d» i eksporten — en mengde ingen har avtalt, og som ikke er
      // avrundbar uten å endre beløpet kunden faktureres. Timer er den
      // eneste enheten som kan bære summen eksakt. Valgt av Erik 23.09.
      const totalHours = quantityToHours(target.quantity ?? 0, targetUnit) + autoHours;
      const hourly = hourlyBasisFromUnitPrice(targetUnit, target.unit_price ?? 0);
      filtered[targetIdx] = {
        ...target,
        unit: "t",
        quantity: totalHours,
        ...(hourly !== null ? { unit_price: Math.round(hourly) } : {}),
        amount: (target.amount ?? 0) + (auto.amount ?? 0),
      };
      return filtered;
    }
    // stk/fast: ikke bland mengder — vis auto-linja som egen post
    // (omdøpt) slik at timene ikke forsvinner fra dokumentet.
    const promoted = {
      ...auto,
      description: "Prosjektledelse",
      is_auto_generated: false,
      auto_kind: null,
    };
    return [filtered[targetIdx], promoted, ...filtered.filter((_, i) => i !== targetIdx)];
  }

  // Ingen Prosjektledelse-linje fra før — vis auto-linja som
  // "Prosjektledelse" (uten auto-flag så det ikke trigger låsing).
  const promoted = {
    ...auto,
    description: "Prosjektledelse",
    is_auto_generated: false,
    auto_kind: null,
  };
  return [promoted, ...filtered];
}

function quantityToHours(qty: number, unit: string): number {
  const u = normalizeOfferUnit(unit);
  if (u === "d") return qty * BILLABLE_HOURS_PER_DAY;
  return qty;
}

function hoursToUnit(hours: number, unit: string): number {
  const u = normalizeOfferUnit(unit);
  if (u === "d") return hours / BILLABLE_HOURS_PER_DAY;
  return hours;
}
